import React, { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { BackBar } from "../../components/BackBar";
import { CheckCircle2, Clock, Circle, CreditCard, Calendar, TrendingUp, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const MODE_LABEL = {
  annual: { fr: "Annuel · 1 versement", en: "Annual · 1 payment" },
  biannual: { fr: "Semestriel · 2 versements", en: "Biannual · 2 payments" },
  monthly: { fr: "Mensuel · 10 versements", en: "Monthly · 10 payments" },
};

export default function PaymentPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    api.get("/payments/plan/mine")
      .then(r => setPlans(r.data.plans || []))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (n) => Number(n).toLocaleString(lang === "fr" ? "fr-FR" : "en-US");
  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US",
      { day: "2-digit", month: "short", year: "numeric" });
  };

  const payInstallment = async (enrollmentId) => {
    setPaying(enrollmentId);
    try {
      const { data } = await api.post("/payments/checkout", {
        enrollment_id: enrollmentId, origin_url: window.location.origin,
      });
      if (data?.url) { window.location.href = data.url; return; }
      toast.error(lang === "fr" ? "Erreur Stripe" : "Stripe error");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPaying(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-8" data-testid="parent-payment-page">
      <BackBar to="/parent" fallbackLabelFr="Tableau de bord" fallbackLabelEn="Dashboard" />
      <p className="overline mb-2">{lang === "fr" ? "Mon paiement" : "My payment"}</p>
      <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">
        {lang === "fr" ? "Plan de paiement" : "Payment plan"}
      </h1>
      <p className="text-sm text-muted-foreground mb-10 max-w-2xl">
        {lang === "fr"
          ? "Suivi de vos échéances : acompte versé, échéances payées, à venir, et prochaine échéance à régler."
          : "Track your installments: deposit paid, paid installments, upcoming, and next due."}
      </p>

      {loading && <div className="text-muted-foreground text-sm">{lang === "fr" ? "Chargement…" : "Loading…"}</div>}

      {!loading && plans.length === 0 && (
        <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">
          {lang === "fr"
            ? "Aucun plan de paiement actif pour le moment. Il apparaîtra ici dès que l'acompte d'inscription sera réglé."
            : "No active payment plan yet. It will appear here once the enrollment deposit is paid."}
        </CardContent></Card>
      )}

      {plans.map(plan => {
        const progressPct = plan.installments_count
          ? Math.round((plan.installments_paid / plan.installments_count) * 100) : 0;
        return (
        <Card key={plan.enrollment_id} className="mb-8 border-accent/30" data-testid={`payment-plan-${plan.enrollment_id}`}>
          <CardHeader>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <CardTitle className="font-display tracking-tight text-2xl">{plan.student_name || "—"}</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {plan.student_grade} · {plan.courses} {lang === "fr" ? "cours" : "course(s)"} · {plan.annual_per_course?.toLocaleString("fr-FR")} € / {lang === "fr" ? "an" : "year"}
                </p>
              </div>
              <Badge className="bg-accent text-accent-foreground hover:bg-accent">
                {MODE_LABEL[plan.mode]?.[lang] || plan.mode}
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* SEPA mandate banner — only for 2× / 10× plans */}
            {plan.sepa_required && (
              plan.sepa_mandate_active ? (
                <div className="flex items-start gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid={`sepa-active-${plan.enrollment_id}`}>
                  <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="text-sm flex-1">
                    <div className="font-semibold text-emerald-900">
                      {lang === "fr" ? "Prélèvement automatique actif" : "Direct debit active"}
                    </div>
                    <p className="text-emerald-900/80 mt-0.5">
                      {lang === "fr"
                        ? `Vos échéances seront débitées automatiquement sur le 15 de chaque mois.${plan.sepa_last4 ? ` IBAN •••• ${plan.sepa_last4}` : ""}`
                        : `Your installments will be debited automatically on the 15th of each month.${plan.sepa_last4 ? ` IBAN •••• ${plan.sepa_last4}` : ""}`}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/[0.04] p-4" data-testid={`sepa-required-${plan.enrollment_id}`}>
                  <AlertCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div className="text-sm flex-1">
                    <div className="font-semibold text-primary">
                      {lang === "fr" ? "Action requise · mandat SEPA" : "Action required · SEPA mandate"}
                    </div>
                    <p className="text-primary/80 mt-0.5">
                      {lang === "fr"
                        ? "Votre plan prévoit des échéances automatiques. Veuillez autoriser les prélèvements en quelques clics."
                        : "Your plan includes automatic installments. Please authorize the direct debits in a few clicks."}
                    </p>
                  </div>
                  <Button size="sm" onClick={() => navigate(`/parent/sepa-mandate/${plan.enrollment_id}`)}
                          data-testid={`sepa-setup-${plan.enrollment_id}`}>
                    <ShieldCheck className="w-4 h-4 mr-1.5" />
                    {lang === "fr" ? "Configurer" : "Set up"}
                  </Button>
                </div>
              )
            )}

            {/* Top stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat icon={CreditCard} label={lang === "fr" ? "Total annuel" : "Annual total"} value={`${fmt(plan.annual_total)} €`} />
              <Stat icon={CheckCircle2} label={lang === "fr" ? "Acompte versé" : "Deposit paid"} value={`${fmt(plan.deposit_total)} €`} color="text-accent" />
              <Stat icon={TrendingUp} label={lang === "fr" ? "Solde payé" : "Balance paid"} value={`${fmt(plan.balance_paid)} €`} color="text-accent" />
              <Stat icon={Clock} label={lang === "fr" ? "Solde restant" : "Remaining"} value={`${fmt(plan.balance_remaining)} €`} />
            </div>

            {/* Progress bar */}
            <div>
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                <span>{lang === "fr" ? "Progression" : "Progress"}</span>
                <span className="tabular-nums font-mono">
                  {plan.installments_paid} / {plan.installments_count} {lang === "fr" ? "échéances" : "installments"} ({progressPct} %)
                </span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div
                  className="h-full bg-accent transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                  data-testid={`progress-${plan.enrollment_id}`}
                />
              </div>
            </div>

            {/* Schedule */}
            <div>
              <h3 className="font-display font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {lang === "fr" ? `Calendrier — ${plan.installments_count} échéance${plan.installments_count > 1 ? "s" : ""} de ${fmt(plan.installment_amount)} €` : `Schedule — ${plan.installments_count} installment${plan.installments_count > 1 ? "s" : ""} of €${fmt(plan.installment_amount)}`}
              </h3>

              <div className="space-y-2">
                {/* Deposit row (always paid here since we only list plans with deposit_paid=true) */}
                <ScheduleRow
                  lang={lang}
                  index={0}
                  label={lang === "fr" ? "Acompte" : "Deposit"}
                  amount={plan.deposit_total}
                  date={plan.deposit_paid_at}
                  status="paid"
                  fmt={fmt} fmtDate={fmtDate}
                />
                {plan.schedule.map((inst) => (
                  <ScheduleRow
                    key={inst.index}
                    lang={lang}
                    index={inst.index}
                    label={
                      plan.mode === "monthly"
                        ? (lang === "fr" ? `Mensualité ${inst.index} / ${inst.total}` : `Monthly ${inst.index} / ${inst.total}`)
                        : plan.mode === "biannual"
                        ? (lang === "fr" ? `Semestre ${inst.index} / ${inst.total}` : `Semester ${inst.index} / ${inst.total}`)
                        : (lang === "fr" ? "Versement annuel" : "Annual payment")
                    }
                    amount={inst.amount}
                    date={inst.paid_at || inst.due_date}
                    status={inst.status}
                    fmt={fmt} fmtDate={fmtDate}
                    testid={`schedule-${plan.enrollment_id}-${inst.index}`}
                  />
                ))}
              </div>
            </div>

            {/* Pay next installment CTA */}
            {plan.balance_remaining > 0 && plan.schedule.some(s => s.status === "due") && (
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-primary/5 border border-primary/30">
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-primary mb-0.5">
                    {lang === "fr" ? "Prochaine échéance à régler" : "Next installment due"}
                  </div>
                  <div className="text-sm">
                    {fmt(plan.installment_amount)} € — {fmtDate(plan.schedule.find(s => s.status === "due")?.due_date)}
                  </div>
                </div>
                <Button
                  data-testid={`pay-next-${plan.enrollment_id}`}
                  onClick={() => payInstallment(plan.enrollment_id)}
                  disabled={paying === plan.enrollment_id}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
                >
                  {paying === plan.enrollment_id
                    ? (lang === "fr" ? "Redirection…" : "Redirecting…")
                    : (lang === "fr" ? `Payer ${fmt(plan.installment_amount)} €` : `Pay €${fmt(plan.installment_amount)}`)}
                </Button>
              </div>
            )}

            {plan.balance_remaining <= 0 && (
              <div className="p-4 rounded-xl bg-accent/10 border border-accent/40 text-center">
                <CheckCircle2 className="w-6 h-6 text-accent mx-auto mb-2" />
                <div className="font-display font-semibold text-accent">
                  {lang === "fr" ? "Plan de paiement intégralement réglé · Merci !" : "Payment plan fully paid · Thank you!"}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-border rounded-xl p-3">
      <Icon className={`w-4 h-4 mb-2 ${color || "text-muted-foreground"}`} strokeWidth={1.5} />
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display font-semibold text-lg tabular-nums ${color || ""}`}>{value}</div>
    </div>
  );
}

function ScheduleRow({ lang, label, amount, date, status, fmt, fmtDate, testid }) {
  const cfg = {
    paid: { icon: CheckCircle2, color: "text-accent", bg: "bg-accent/5", border: "border-accent/30", badge: "default", label: lang === "fr" ? "Payée" : "Paid" },
    due: { icon: Clock, color: "text-primary", bg: "bg-primary/5", border: "border-primary/40", badge: "default", label: lang === "fr" ? "À régler" : "Due" },
    upcoming: { icon: Circle, color: "text-muted-foreground", bg: "bg-card", border: "border-border", badge: "secondary", label: lang === "fr" ? "À venir" : "Upcoming" },
  }[status] || {};
  const Icon = cfg.icon || Circle;
  return (
    <div
      data-testid={testid}
      className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${cfg.border} ${cfg.bg} transition-colors`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon className={`w-5 h-5 shrink-0 ${cfg.color}`} strokeWidth={status === "upcoming" ? 1.5 : 2} />
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{label}</div>
          <div className="text-xs text-muted-foreground">
            {status === "paid" ? (lang === "fr" ? "Réglée le " : "Paid on ") : (lang === "fr" ? "Échéance " : "Due ")}
            {fmtDate(date)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="font-display font-semibold tabular-nums text-sm">{fmt(amount)} €</div>
        <Badge variant={cfg.badge} className="text-[10px]">{cfg.label}</Badge>
      </div>
    </div>
  );
}
